import { isValid, parse, parseISO } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

/**
 * Helper function to get user's locale and timezone
 */
const getUserLocaleAndTimezone = (): { locale: string; timeZone: string } => {
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const locale = (typeof navigator !== 'undefined' && (navigator.languages?.[0])) || Intl.DateTimeFormat().resolvedOptions().locale;
  return { locale, timeZone };
};

/** e.g. "August 2016", "Aug 2016" (month + year, no day) */
const MONTH_YEAR_ONLY = /^[A-Za-z]+\s+\d{4}$/;

const tryParseDate = (date: string): Date | null => {
  const trimmed = date.trim();
  if (!trimmed) return null;

  // Handle "YYYY-MM-DD HH:mm:ss" format as UTC
  if (trimmed.match(/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/)) {
    const parsed = parseISO(trimmed.replace(' ', 'T') + 'Z');
    return isValid(parsed) ? parsed : null;
  }

  if (trimmed.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
    // Support locale-like dates such as "26/05/2026"
    let parsed = parse(trimmed, 'dd/MM/yyyy', new Date());
    if (!isValid(parsed)) {
      parsed = parse(trimmed, 'MM/dd/yyyy', new Date());
    }
    return isValid(parsed) ? parsed : null;
  }

  if (MONTH_YEAR_ONLY.test(trimmed)) {
    let parsed = parse(trimmed, 'MMMM yyyy', new Date());
    if (!isValid(parsed)) {
      parsed = parse(trimmed, 'MMM yyyy', new Date());
    }
    return isValid(parsed) ? parsed : null;
  }

  const isoParsed = parseISO(trimmed);
  if (isValid(isoParsed)) {
    return isoParsed;
  }

  const nativeParsed = new Date(trimmed);
  return Number.isNaN(nativeParsed.getTime()) ? null : nativeParsed;
};

/**
 * Helper function to parse and zone a date string
 */
const parseAndZoneDate = (date: string, timeZone?: string): Date => {
  const parsed = tryParseDate(date);
  if (!parsed) {
    throw new RangeError(`Invalid date value: ${date}`);
  }

  const { timeZone: userTimeZone } = getUserLocaleAndTimezone();
  return toZonedTime(parsed, timeZone || userTimeZone);
};

/**
 * Helper function to format a date with common options
 */
const formatDateWithOptions = (
  date: Date,
  options: Intl.DateTimeFormatOptions,
  locale?: string
): string => {
  const { locale: userLocale } = getUserLocaleAndTimezone();
  return new Intl.DateTimeFormat(locale || userLocale, options).format(date);
};

/**
 * Converts any ISO date string into the user's locale & zone,
 * showing full date + time.
 */
const ConvertToLocalDate = (date: string): string => {
  try {
    if (!date) return 'NA';
    const localDate = parseAndZoneDate(date);
    const datePart = formatDateWithOptions(localDate, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    const timePart = formatDateWithOptions(localDate, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return `${datePart} at ${timePart}`;
  } catch (error) {
    console.error("ConvertToLocalDate invalid input:", error);
    return 'NA';
  }
};

/**
 * Converts any ISO date string into the user's locale & zone,
 * showing only the date part (no time). Same long format as ConvertToLocalDate.
 */
const ConvertToLocalDateOnly = (date: string): string => {
  try {
    const localDate = parseAndZoneDate(date);
    return formatDateWithOptions(localDate, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  } catch (error) {
    console.error("ConvertToLocalDateOnly invalid input:", error);
    return 'NA';
  }
};

/**
 * Converts ISO date to a "MMM DD" style, in the user's locale & zone.
 */
const ConvertToLocalDateMonthDate = (date: string): string => {
  try {
    const localDate = parseAndZoneDate(date);
    return formatDateWithOptions(localDate, {
      month: 'short',
      day: '2-digit'
    });
  } catch (error) {
    console.error("ConvertToLocalDateMonthDate invalid input:", error);
    return 'NA';
  }
};

/**
 * Converts ISO date to full numeric date + time (MM/DD/YYYY, hh:mm:ss),
 * using 12-hour format.
 */
const convertToLocalDayMonthYear = (date: string): string => {
  try {
    const localDate = parseAndZoneDate(date);
    const datePart = formatDateWithOptions(localDate, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timePart = formatDateWithOptions(localDate, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  } catch (error) {
    console.error("convertToLocalDayMonthYear invalid input:", error);
    return date ?? '';
  }
};

/**
 * Converts ISO date to MM/DD/YYYY format for coverage validation dates
 */
function formatCoverageDate(date: string): string {
  try {
    const trimmed = String(date ?? '').trim();
    if (!trimmed) return '';

    const localDate = parseAndZoneDate(trimmed);

    // Month/year labels (e.g. prod_date: "August 2016") — avoid inventing a day
    if (MONTH_YEAR_ONLY.test(trimmed)) {
      return formatDateWithOptions(localDate, {
        year: 'numeric',
        month: 'long',
      });
    }

    return formatDateWithOptions(localDate, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  } catch (error) {
    console.error("formatCoverageDate invalid input:", error);
    return String(date ?? '').trim();
  }
}

/**
 * For PowerBI dates: always show in America/New_York,
 * but still use Intl to respect U.S. conventions.
 */
const ConvertToLocalDatePowerBI = (date: string): string => {
  try {
    const zonedDate = parseAndZoneDate(date, 'America/New_York');
    return formatDateWithOptions(zonedDate, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    }, 'en-US') + ' EST';
  } catch (error) {
    console.error("ConvertToLocalDatePowerBI invalid input:", error);
    return date ?? '';
  }
};

/** "YYYY-MM-DD HH:mm:ss" with optional fractional seconds */
const DATETIME_12H_PATTERN = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(?:\.\d+)?$/;

/**
 * Formats a "YYYY-MM-DD HH:mm:ss" date string in 12-hour format with AM/PM.
 * The time is shown as-is (no UTC → local timezone conversion).
 * e.g. "2026-06-25 14:04:17" → "06/25/2026, 02:04:17 PM" (locale-dependent)
 */
const formatDateTime12Hour = (date: string): string => {
  try {
    const trimmed = String(date ?? '').trim();
    if (!trimmed) return '';

    const match = trimmed.match(DATETIME_12H_PATTERN);
    const displayDate = match
      ? parse(`${match[1]} ${match[2]}`, 'yyyy-MM-dd HH:mm:ss', new Date())
      : parseAndZoneDate(trimmed);

    if (!isValid(displayDate)) return trimmed;

    const datePart = formatDateWithOptions(displayDate, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timePart = formatDateWithOptions(displayDate, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  } catch (error) {
    console.error('formatDateTime12Hour invalid input:', error);
    return String(date ?? '').trim();
  }
};

/**
 * Parses a "YYYY-MM-DD hh:mm:ss.SSSSSS" UTC string,
 * then formats it in the user's locale & zone using 12-hour format.
 */
const conversationHistoryTimezone = (dateString: string): string => formatDateTime12Hour(dateString);

/**
 * Given a JS Date, emits formatted date/time
 * in the user's locale & zone using 12-hour format.
 */
const getCurrentDateTime = (date: Date): string => {
  try {
    const datePart = formatDateWithOptions(date, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
    const timePart = formatDateWithOptions(date, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
    return `${datePart}, ${timePart}`;
  } catch (error) {
    console.error("getCurrentDateTime invalid input:", error);
    return '';
  }
};

/**
 * Formats seconds into MM:SS.mm format with locale-aware formatting
 */
const formatTimestamp = (seconds: number): string => {
  try {
    const { locale } = getUserLocaleAndTimezone();
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    const milliseconds = Math.floor((seconds % 1) * 100);

    if (minutes === 0) {
      return new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(remainingSeconds + milliseconds / 100);
    }

    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error("formatTimestamp invalid input:", error);
    return '0.00';
  }
};

/**
 * Formats elapsed time for user-facing labels (e.g. "Answered in …").
 * - Over 60s: "1 minute 33 seconds" (no MM:SS — avoids "1:33 seconds" confusion)
 * - 60s or less: decimal seconds, e.g. "28.12 seconds"
 */
/** Agent run length from `duration_ms`: `55s`, `1m 23s`, `2m 12s`. */
const formatAgentRunDuration = (durationMs: number): string => {
  let totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  if (durationMs > 0 && totalSeconds === 0) totalSeconds = 1;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes <= 0) return `${seconds}s`;
  if (seconds === 0) return `${minutes}m`;
  return `${minutes}m ${seconds}s`;
};

const formatElapsedTimeLabel = (seconds: number): string => {
  try {
    if (!Number.isFinite(seconds) || seconds < 0) {
      return '0 seconds';
    }

    if (seconds > 60) {
      const minutes = Math.floor(seconds / 60);
      const remainderSeconds = Math.floor(seconds % 60);
      const minuteUnit = minutes === 1 ? 'minute' : 'minutes';
      const parts = [`${minutes} ${minuteUnit}`];

      if (remainderSeconds > 0) {
        const secondUnit = remainderSeconds === 1 ? 'second' : 'seconds';
        parts.push(`${remainderSeconds} ${secondUnit}`);
      }

      return parts.join(' ');
    }

    const { locale } = getUserLocaleAndTimezone();
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(seconds);
    const secondUnit = seconds === 1 ? 'second' : 'seconds';
    return `${formatted} ${secondUnit}`;
  } catch (error) {
    console.error('formatElapsedTimeLabel invalid input:', error);
    return '0 seconds';
  }
};

/**
 * Formats a plain number according to the user's locale.
 */
const formatNumber = (value: number, options?: Intl.NumberFormatOptions): string => {
  try {
    const { locale } = getUserLocaleAndTimezone();
    return new Intl.NumberFormat(locale, options).format(value);
  } catch (error) {
    console.error("formatNumber invalid input:", error);
    return 'NA';
  }
};

/**
 * Formats a numeric value as currency according to the user's locale.
 * Currency is derived from the locale (e.g. USD for en-US, EUR for de-DE).
 */
const formatCurrency = (value: number): string => {
  if (value == null ) return '—';
  return formatNumber(value, { style: 'currency', currency: 'USD', currencyDisplay: 'narrowSymbol', minimumFractionDigits: 0, maximumFractionDigits: 2 });
};

/**
 * Formats a fraction (0–1) as a percentage according to the user's locale.
 * Shows 2 decimal places when present.
 */
const formatPercentage = (
  value: number,
  opts?: Pick<Intl.NumberFormatOptions, 'minimumFractionDigits' | 'maximumFractionDigits'>
): string => {
  try {
    const { locale } = getUserLocaleAndTimezone();
    return new Intl.NumberFormat(locale, {
      style: 'percent',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      ...opts
    }).format(value);
  } catch (error) {
    console.error("formatPercentage invalid input:", error);
    return 'NA';
  }
};

/**
 * Parses a score value from various formats (number, percentage string, fraction string)
 * and returns it as a number (0-100 scale for percentages/fractions).
 */
const parseScore = (score: number | string): number => {
  if (typeof score === 'number' && Number.isFinite(score)) {
    return score;
  }

  // Handle percentage format like "85%"
  if (typeof score === 'string' && score.includes('%')) {
    return Number.parseFloat(score.replace('%', '').trim()) || 0;
  }

  // Handle fraction format like "8/10" or "10/10"
  if (typeof score === 'string' && score.includes('/')) {
    const parts = score.split('/').map(s => Number.parseFloat(s.trim()));
    if (parts.length === 2 && parts[1] > 0) {
      return (parts[0] / parts[1]) * 100;
    }
  }

  // Try parsing as regular number
  return Number.parseFloat(String(score)) || 0;
};

/**
 * Formats a YYYY-MM-DD date string according to the user's browser locale
 * (e.g. dd/mm/yyyy for India, mm/dd/yyyy for USA).
 */
const formatDateLocale = (dateStr: string): string => {
  const datestring = dateStr.toString();
  if (!datestring) return '';
  const d = new Date(datestring + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return datestring;
  const { locale } = getUserLocaleAndTimezone();
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric', month: '2-digit', day: '2-digit'
  }).format(d);
};

export {
  ConvertToLocalDateMonthDate,
  ConvertToLocalDate,
  ConvertToLocalDateOnly,
  convertToLocalDayMonthYear,
  formatDateTime12Hour,
  ConvertToLocalDatePowerBI,
  conversationHistoryTimezone,
  getCurrentDateTime,
  formatTimestamp,
  formatAgentRunDuration,
  formatElapsedTimeLabel,
  formatNumber,
  formatPercentage,
  formatCoverageDate,
  parseScore,
  formatCurrency,
  formatDateLocale,
  getUserLocaleAndTimezone
};